import React, { useRef, useState } from "react";
import {
  Card,
  CardStackSection,
  Stack,
  Text,
  Box,
  Inline,
  AlertBanner,
  Alert,
} from "@origin-digital/ods-core";
import { IconLock } from "@origin-digital/ods-icons";
import { Formik, Form } from "formik";
import ReCAPTCHA from "react-google-recaptcha";
import envConfig from "src/envConfig/envHelper";
import { apiClientPublic } from "src/api/apiService";

import EICFormPageOne from "./EICFormPageOne";
import EICFormPageThree from "./EICFormPageThree";
import EICFormPageFour from "./EICFormPageFour";
import EICFormPageFive from "./EICFormPageFive";
import EICSuccessPage from "./EICSuccessPage";
import EICFailurePage from "./EICFailurePage";
import EICSubmittedPage from "./EICSubmittedPage";
import {
  pageOneValidationSchema,
  pageTwoValidationSchema,
  pageThreeValidationSchema,
  pageThreeReadOnlyValidationSchema,
} from "./validationSchema";
import * as Yup from "yup";
import { getIsGasLineOnly, mapFormDataToAPI } from "./helper";
import { EICFormData } from "./types";
import { ConnectionTypeEnum } from "src/components/Premises/types";
import EICFormPageTwoReadOnly from "./EICFormPageTwoReadOnly";
import EICFormPageTwo from "./EICFormPageTwo";
import { mapKeyToConnectionTypeLabel } from "../helper";
import EICFormPageThreeReadOnly from "./EICFormPageThreeReadOnly";

// Default values for postal address fields
export const postalAddressFields = {
  postalUnitNo: "",
  postalPremiseType: "",
  postalLevelNo: "",
  postalStreetNo: "",
  postalStreetName: "",
  postalStreetType: "",
  postalSuffix: "",
  postalSuburb: "",
  postalState: "",
  postalPostcode: "",
};
// export const postalAddressFields = {
//   postalUnitNo: "1",
//   postalPremiseType: "Unit",
//   postalLevelNo: "1",
//   postalStreetNo: "22",
//   postalStreetName: "Adelaide",
//   postalStreetType: "Street",
//   postalSuffix: "",
//   postalSuburb: "Brisbane",
//   postalState: "QLD",
//   postalPostcode: "4000",
// };

// Default values for PO Box fields
export const poBoxFields = {
  poBoxType: "",
  poBoxNo: "",
  poBoxSuburb: "",
  poBoxState: "",
  poBoxPostcode: "",
};
// export const poBoxFields = {
//   poBoxType: "CPA",
//   poBoxNo: "123",
//   poBoxSuburb: "Brisbane",
//   poBoxState: "QLD",
//   poBoxPostcode: "4000",
// };

export interface EICFormErrors {
  referenceNumber?: string;
  postcode?: string;
  accountType?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  phone?: string;
  email?: string;
  companyName?: string;
  abn?: string;
  addSecondaryAccountHolder?: string;
  secondaryFirstName?: string;
  secondaryLastName?: string;
  secondaryDateOfBirth?: string;
  secondaryPhone?: string;
  secondaryEmail?: string;
  addSecondaryToBills?: string;
  // Page 3 errors
  billDeliveryMethod?: string;
  postalAddressType?: string;
  lifeSupport?: string;
  whichEnergy?: string;
  // concession errors (string error messages from Yup)
  concessionStatus?: string;
  concessionCardType?: string;
  concessionNumber?: string;
  consentElectronicSignature?: string;
  consentLegallyBinding?: string;
}

type SubmissionStatus = "form" | "success" | "failure" | "submitted";

const getValidationSchema = (currentPage: number, isReadonlyFlow: boolean) => {
  switch (currentPage) {
    case 1:
      return pageOneValidationSchema;
    case 2:
      // Skip validation for readonly page 2 since it has no editable fields
      return isReadonlyFlow ? Yup.object({}) : pageTwoValidationSchema;
    case 3:
      // Use specialized validation for readonly page 3 (only life support fields)
      return isReadonlyFlow
        ? pageThreeReadOnlyValidationSchema
        : pageThreeValidationSchema;
    case 4:
      return Yup.object({});
    case 5:
      return Yup.object({});
    default:
      return pageOneValidationSchema;
  }
};

const EICPublicForm: React.FC = () => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  // start on page 1
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasApiError, setHasApiError] = useState<boolean>(false);
  const [isAlertBannerVisible, setIsAlertBannerVisible] =
    useState<boolean>(true);
  const [submissionStatus, setSubmissionStatus] =
    useState<SubmissionStatus>("form");
  const [submittedFormData, setSubmittedFormData] =
    useState<EICFormData | null>(null);

  const [isReadonlyFlow, setIsReadonlyFlow] = useState<boolean>(false);
  const [acceptedConnectionType, setAcceptedConnectionType] =
    useState<ConnectionTypeEnum | null>(ConnectionTypeEnum.SERVICE_CONNECTION);

  const initialValues: EICFormData = {
    referenceNumber: "",
    postcode: "",
    connectionTypes: [],
    // Page 2 initial values
    address: "",
    state: "",
    accountType: "personal",
    firstName: "",
    lastName: "",
    dateOfBirth: null,
    phone: "",
    email: "",
    companyName: "",
    abn: "",
    addSecondaryAccountHolder: false,
    secondaryFirstName: "",
    secondaryLastName: "",
    secondaryDateOfBirth: null,
    secondaryPhone: "",
    secondaryEmail: "",
    addSecondaryToBills: false,
    // Page 3 initial values
    billDeliveryMethod: "post",
    postalAddressType: "premiseAddress",
    lifeSupport: "",
    whichEnergy: "",
    // Postal address and PO Box initial values
    ...postalAddressFields,
    ...poBoxFields,
    // Concession block defaults (status: null = unset)
    concessionStatus: null,
    concessionCardType: "",
    concessionNumber: "",
    consentElectronicSignature: false,
    consentLegallyBinding: false,
    // Services Australia consent
    consentElectronicSignatureServicesAustralia: false,
    consentLegallyBindingServicesAustralia: false,
  };
  // const initialValues: EICFormData = {
  //   referenceNumber: "123",
  //   postcode: "2000",
  //   connectionTypes: [ConnectionTypeEnum.SERVICE_CONNECTION],
  //   // Page 2 initial values
  //   address: "22 adelaide street, brisbane, vic 4000",
  //   state: State.VIC,
  //   accountType: "business",
  //   firstName: "John",
  //   lastName: "Doe",
  //   dateOfBirth: new Date("1990-01-01"),
  //   phone: "0412345678",
  //   email: "john.doe@example.com",
  //   companyName: "Acme Inc",
  //   abn: "12345678901",
  //   addSecondaryAccountHolder: false,
  //   secondaryFirstName: "Jane",
  //   secondaryLastName: "Doe",
  //   secondaryDateOfBirth: new Date("1992-05-15"),
  //   secondaryPhone: "0412345679",
  //   secondaryEmail: "jane.doe@example.com",
  //   addSecondaryToBills: true,
  //   // Page 3 initial values
  //   billDeliveryMethod: "email",
  //   postalAddressType: "premiseAddress",
  //   lifeSupport: "",
  //   whichEnergy: "electricity",
  //   // Postal address and PO Box initial values
  //   ...postalAddressFields,
  //   ...poBoxFields,
  // };

  const handleContinue = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentPage(currentPage + 1);
  };

  const handleBack = () => {
    setCurrentPage(currentPage - 1);
  };

  const handleEdit = (page: number) => {
    setCurrentPage(page);
  };

  const handleGoBackFromFailure = () => {
    setSubmissionStatus("form");
    setCurrentPage(5);
  };

  const handleGoBackFromSubmitted = () => {
    setSubmissionStatus("form");
    setCurrentPage(1);
  };

  const handleAlreadySubmitted = () => {
    setSubmissionStatus("submitted");
  };

  const handleApiError = (hasError: boolean) => {
    setHasApiError(hasError);
    if (hasError) {
      setIsAlertBannerVisible(true); // Show banner when new API error occurs
    }
  };

  const handleSubmit = async (values: EICFormData) => {
    try {
      // Execute reCAPTCHA to get token
      let recaptchaToken: string | null = null;
      if (recaptchaRef.current) {
        recaptchaToken = await recaptchaRef.current.executeAsync();
        recaptchaRef.current.reset();
      }

      // Map form data to API schema
      const apiData = mapFormDataToAPI(values, recaptchaToken || undefined);

      // Make API call
      const response = await apiClientPublic.post(
        "/eic/new-connection",
        apiData
      );

      console.log("Form submitted successfully:", response);

      // Show success page
      setSubmittedFormData(values);
      setSubmissionStatus("success");
    } catch (error) {
      console.error("Form submission failed:", error);

      // Preserve form values for when user goes back
      setSubmittedFormData(values);
      // Show failure page
      setSubmissionStatus("failure");
    }
  };

  // If submission status is success, failure, or submitted, show the respective page
  if (submissionStatus === "success" && submittedFormData) {
    return <EICSuccessPage formData={submittedFormData} />;
  }

  if (submissionStatus === "failure") {
    return <EICFailurePage onGoBack={handleGoBackFromFailure} />;
  }

  if (submissionStatus === "submitted") {
    return <EICSubmittedPage onGoBack={handleGoBackFromSubmitted} />;
  }

  const renderCurrentPage = (values: EICFormData) => {
    const isGasLineOnly = getIsGasLineOnly(values.connectionTypes);

    const handleContinueWithSkip = () => {
      // Skip page 4 if it's gas line only
      if (currentPage === 3 && isGasLineOnly) {
        setCurrentPage(5);
      } else {
        setCurrentPage(currentPage + 1);
      }
    };

    const handleBackWithSkip = () => {
      // If we're on page 5 and it's gas line only, go back to page 3
      if (currentPage === 5 && isGasLineOnly) {
        setCurrentPage(3);
      } else {
        setCurrentPage(currentPage - 1);
      }
    };

    switch (currentPage) {
      case 1:
        return (
          <EICFormPageOne
            onContinue={handleContinue}
            setHasApiError={handleApiError}
            onAlreadySubmitted={handleAlreadySubmitted}
            onAcceptedConnectionType={setAcceptedConnectionType}
            onIsReadonlyFlow={setIsReadonlyFlow}
          />
        );
      case 2:
        return isReadonlyFlow ? (
          <EICFormPageTwoReadOnly
            onBack={handleBack}
            onContinue={handleContinue}
          />
        ) : (
          <EICFormPageTwo onBack={handleBack} onContinue={handleContinue} />
        );
      case 3:
        return isReadonlyFlow ? (
          <EICFormPageThreeReadOnly
            onBack={handleBack}
            onContinue={handleContinueWithSkip}
          />
        ) : (
          <EICFormPageThree
            onBack={handleBack}
            onContinue={handleContinueWithSkip}
          />
        );
      case 4:
        return (
          <EICFormPageFour onBack={handleBack} onContinue={handleContinue} />
        );
      case 5:
        return (
          <EICFormPageFive
            onBack={handleBackWithSkip}
            onSubmit={handleSubmit}
            onEdit={handleEdit}
            isReadonlyFlow={isReadonlyFlow}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Stack space="small">
      <Box
        style={{
          display: hasApiError && isAlertBannerVisible ? "block" : "none",
        }}
      >
        <AlertBanner
          title="Oops, those details don't seem to match"
          tone="critical"
          onCloseClick={() => setIsAlertBannerVisible(false)}
        >
          Please check the details provided in the email and try again.
        </AlertBanner>
      </Box>

      <Formik
        initialValues={submittedFormData || initialValues}
        validationSchema={getValidationSchema(currentPage, isReadonlyFlow)}
        onSubmit={() => {}}
        enableReinitialize={true}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ values }) => {
          const isGasLineOnly = getIsGasLineOnly(values.connectionTypes);
          const getDisplayStep = () => {
            if (isGasLineOnly && currentPage === 5) {
              return 4;
            }
            return currentPage;
          };
          const totalSteps = isGasLineOnly ? 4 : 5;

          return (
            <Form>
              <CardStackSection cardWidth="medium">
                <Card padding="medium">
                  <Stack space={["xlarge", "xxlarge"]}>
                    {isReadonlyFlow &&
                      currentPage === 2 &&
                      acceptedConnectionType && (
                        <Alert
                          title={`You’ve already submitted your details for ${mapKeyToConnectionTypeLabel(
                            acceptedConnectionType,
                            true
                          )} connection`}
                        >
                          Your details have been copied over, but we’ll still
                          need you to submit this form and agree to the energy
                          charges, fees and terms. You won’t be able to change
                          some account holder details.
                        </Alert>
                      )}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Text variant="body-small" tone="neutralLight">
                        Step {getDisplayStep()} of {totalSteps}
                      </Text>
                      <Inline space="xsmall" alignY="center">
                        <IconLock size="xsmall" color="neutralLight" />
                        <Text variant="body-small" tone="neutralLight">
                          Secure site
                        </Text>
                      </Inline>
                    </Box>
                    {renderCurrentPage(values)}
                  </Stack>
                </Card>
                <Box />
              </CardStackSection>

              <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey={envConfig.config.reCaptchaSiteKey}
              />
            </Form>
          );
        }}
      </Formik>
    </Stack>
  );
};

export default EICPublicForm;
